/**
 * TaskCard Component
 * Displays an individual task as a compact horizontal row with chip indicators.
 */

import { useDraggable } from '@dnd-kit/core';
import type { Task } from '@signalvote/shared';
import { TaskStatus } from '@signalvote/shared';
import { parseISO, format, getYear } from 'date-fns';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  getAssignmentDirection,
  getAssigneeInitials,
  formatDurationShort,
  formatOverdueAge,
  getCategoryChipClasses,
  getCategoryAbbreviation,
  formatVolunteerDisplayName,
  isExternalUrl,
} from '@/lib/taskAssignmentUtils';
import { formatTime12Hour, getCampaignToday, isToday } from '@/lib/calendarUtils';
import { ExternalLink, ArrowRight, UserPlus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useVolunteers } from '@/hooks/queries';
import { useAuth } from '@/contexts/AuthContext';

// ============================================================================
// Types
// ============================================================================

export interface TaskCardProps {
  /** The task to display */
  task: Task;
  /** Current user's ID for assignment badge logic */
  currentUserId?: string;
  /** Whether this card can be dragged (e.g. from sidebar to calendar) */
  draggable?: boolean;
  /** Called when checkbox is toggled */
  onCheckboxChange: (taskId: string) => void;
  /** Called when Accept button is clicked (only shown for Pending tasks) */
  onAccept?: (taskId: string) => void;
  /** Called when Reject button is clicked (only shown for Pending tasks) */
  onReject?: (taskId: string) => void;
  /** Called when the card is clicked */
  onClick: (task: Task) => void;
  /** Called when an assignee is selected via the inline assign button */
  onAssign?: (taskId: string, assigneeId: string, assigneeName: string) => void;
  /** Hide the duration chip (e.g. in the Overdue section) */
  hideDuration?: boolean;
  /** Show a date/time chip (e.g. "Feb 28" or "Aug 27, 2027 10:30 am") */
  showDateChip?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Compute context label for the right-side chip.
 * Overdue tasks: "3 days ago". Today tasks: "2 PM". Future: omit.
 */
function getContextLabel(task: Task): string | null {
  if (!task.dueDate) return null;

  const overdue = formatOverdueAge(task.dueDate);
  if (overdue) return overdue;

  const due = parseISO(task.dueDate);
  if (isToday(due) && task.dueTime) {
    return formatTime12Hour(task.dueTime);
  }

  return null;
}

/**
 * Format a date chip for "show all tasks" mode.
 * Same year: "Feb 28". Different year: "Aug 27, 2027".
 * Appends time if present: "Aug 27, 2027 10:30 am".
 */
function formatDateChipLabel(dueDate: string, dueTime?: string | null): string {
  const date = parseISO(dueDate);
  const sameYear = getYear(date) === getYear(getCampaignToday());

  let label = sameYear ? format(date, 'MMM d') : format(date, 'MMM d, yyyy');

  if (dueTime) {
    label += ' ' + formatTime12Hour(dueTime).toLowerCase();
  }

  return label;
}

// ============================================================================
// Component
// ============================================================================

export function TaskCard({
  task,
  currentUserId,
  draggable: isDraggableEnabled = false,
  onCheckboxChange,
  onAccept,
  onReject,
  onClick,
  onAssign,
  hideDuration = false,
  showDateChip = false,
}: TaskCardProps) {
  const isTemp = task.id.startsWith('temp-');
  const isCompleted = task.status === TaskStatus.Completed;
  const direction = getAssignmentDirection(task, currentUserId);
  const contextLabel = getContextLabel(task);
  const durationLabel = hideDuration ? null : formatDurationShort(task.durationMinutes);
  const dateChipLabel = showDateChip && task.dueDate ? formatDateChipLabel(task.dueDate, task.dueTime) : null;
  const categoryAbbr = getCategoryAbbreviation(task.categoryName);
  const assigneeInitials = getAssigneeInitials(task, direction);
  const isNewAssignment =
    task.assignedToId === currentUserId &&
    task.createdById !== currentUserId &&
    task.status === TaskStatus.Pending &&
    !task.acknowledgedAt;

  const navigate = useNavigate();
  const [assignOpen, setAssignOpen] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);

  // Listen for pulse event from "My tasks" header button
  useEffect(() => {
    if (!isNewAssignment) return;
    const handler = () => {
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 2000);
    };
    window.addEventListener('pulse-new-tasks', handler);
    return () => window.removeEventListener('pulse-new-tasks', handler);
  }, [isNewAssignment]);
  const { data: volunteers = [] } = useVolunteers();
  const { user } = useAuth();

  // Show assign button when: no assignee, task is not completed, callback provided, not temp
  const showAssignButton = onAssign && !task.assignedToId && !isCompleted && !isTemp;

  // Drag-and-drop support for sidebar → calendar
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-${task.id}`,
    data: { task, source: 'sidebar' },
    disabled: !isDraggableEnabled || isTemp,
  });

  const handleCardClick = () => {
    if (isTemp) return;
    onClick(task);
  };

  // Combine drag props into a single spread when dragging is enabled
  const dragProps = isDraggableEnabled
    ? { ref: setNodeRef, ...listeners, ...attributes }
    : {};

  return (
    <Card
      {...dragProps}
      onClick={handleCardClick}
      className={cn(
        'cursor-pointer transition-shadow hover:shadow-md',
        isCompleted && 'opacity-70',
        isDraggableEnabled && 'cursor-grab',
        isDragging && 'opacity-50',
        isPulsing && 'animate-task-pulse'
      )}
    >
      <div className="py-1.5 px-2.5">
        <div className="flex items-center gap-1.5">
          {/* Checkbox */}
          <Checkbox
            checked={isCompleted}
            disabled={isTemp}
            onCheckedChange={() => { if (!isTemp) onCheckboxChange(task.id); }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className={cn(
              'shrink-0',
              isCompleted && 'data-[state=checked]:bg-success data-[state=checked]:border-success'
            )}
          />

          {/* New assignment indicator */}
          {isNewAssignment && (
            <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
          )}

          {/* Title - single line truncate */}
          <span
            className={cn(
              'text-sm font-medium leading-tight truncate flex-1 min-w-0',
              isCompleted
                ? 'line-through text-muted-foreground'
                : 'text-card-foreground'
            )}
          >
            {task.title}
          </span>

          {/* Chips container */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Date chip: shown in "show all tasks" mode */}
            {dateChipLabel && (
              <span className="text-[0.6rem] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">
                {dateChipLabel}
              </span>
            )}

            {/* Context chip: overdue age or time (hidden when date chip is shown) */}
            {!dateChipLabel && contextLabel && (
              <span className="text-[0.6rem] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">
                {contextLabel}
              </span>
            )}

            {/* Duration chip */}
            {durationLabel && (
              <span className="text-[0.6rem] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">
                {durationLabel}
              </span>
            )}

            {/* Category chip */}
            {categoryAbbr && (
              <span className={cn(getCategoryChipClasses(task.categoryName), 'whitespace-nowrap')}>
                {categoryAbbr}
              </span>
            )}

            {/* Assignment direction chip */}
            {direction === 'inbound' && (
              <span className="text-[0.6rem] px-1.5 py-0.5 rounded border border-blue-400 text-blue-700 bg-blue-50 font-medium whitespace-nowrap">
                &larr; {assigneeInitials}
              </span>
            )}
            {direction === 'outbound' && (
              <span className="text-[0.6rem] px-1.5 py-0.5 rounded border border-violet-400 text-violet-700 bg-violet-50 font-medium whitespace-nowrap">
                &rarr; {assigneeInitials}
              </span>
            )}

            {/* Inline assign ghost button for unassigned tasks */}
            {showAssignButton && (
              <Popover open={assignOpen} onOpenChange={setAssignOpen}>
                <PopoverTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAssignOpen(true);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="text-[0.6rem] px-1.5 py-0.5 rounded text-muted-foreground/60 hover:text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap flex items-center gap-0.5"
                  >
                    <UserPlus className="h-3 w-3" />
                    Assign
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  sideOffset={4}
                  className="w-48 p-0"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="py-1 max-h-[200px] overflow-y-auto">
                    {volunteers
                      .filter((v) => v.userId !== user?.id)
                      .slice(0, 8)
                      .map((v) => (
                        <button
                          key={v.userId}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssign!(task.id, v.userId, formatVolunteerDisplayName(v.firstName, v.lastName));
                            setAssignOpen(false);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm cursor-pointer hover:bg-accent transition-colors"
                        >
                          <span className="truncate">
                            {v.firstName} {v.lastName}
                          </span>
                          <span className="text-xs text-muted-foreground capitalize ml-auto">
                            {v.isOwner ? 'Owner' : (v.presetKey ?? 'Volunteer')}
                          </span>
                        </button>
                      ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Action URL link - only for tasks with duration >= 30min */}
            {task.actionUrl && (task.durationMinutes ?? 60) >= 30 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isExternalUrl(task.actionUrl!)) {
                    window.open(task.actionUrl!, '_blank', 'noopener,noreferrer');
                  } else {
                    navigate({ to: task.actionUrl! });
                  }
                }}
                onPointerDown={(e) => e.stopPropagation()}
                className="text-[0.6rem] px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                {isExternalUrl(task.actionUrl!) ? (
                  <ExternalLink className="h-3 w-3" />
                ) : (
                  <ArrowRight className="h-3 w-3" />
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </Card>
  );
}

export default TaskCard;
